import React from "react";
import PropTypes from "prop-types";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import ReactCrop, { Crop } from "react-image-crop";
import { DialogActions } from "@mui/material";
import Button from "../CustomButtons/Button";
import { useDialog } from "../../licensing/utils/DialogProvider";
import TranslationWrapper from "../TranslationWrapper/TranslationWrapper";

function CropImageDialog(props: { imagePreviewUrl: string; setCroppedImage: (url: string) => void }) {
  const [croppedImageUrl, setCroppedImageUrl] = React.useState<string | null>(null);
  const [crop, setCrop] = React.useState<Crop | null>(null);
  const [, closeDialog] = useDialog();

  const imageRef = React.useRef<null | HTMLImageElement>(null);

  return (
    <>
      <DialogTitle id={"alert-dialog-title"}>
        <TranslationWrapper tDefault={"Crop Image"} tKey={"cropimage.title"} />
      </DialogTitle>

      <DialogContent>
        <div style={{ display: "flex", justifyContent: "center" }}>
          {props.imagePreviewUrl ? (
            <ReactCrop
              crop={crop ?? {}}
              onChange={(crop) => {
                setCrop(crop);
              }}
              onComplete={(crop) => {
                if (imageRef.current && crop.width && crop.height) {
                  const canvas = document.createElement("canvas");
                  const pixelRatio = window.devicePixelRatio;
                  const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
                  const scaleY = imageRef.current.naturalHeight / imageRef.current.height;
                  const ctx = canvas.getContext("2d");

                  if (ctx === null) {
                    throw new Error("Browser does not support cropping due to lack of support for canvas context2d");
                  }

                  canvas.width = crop.width * pixelRatio * scaleX;
                  canvas.height = crop.height * pixelRatio * scaleY;

                  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
                  ctx.imageSmoothingQuality = "high";

                  ctx.drawImage(
                    imageRef.current,
                    crop.x * scaleX,
                    crop.y * scaleY,
                    crop.width * scaleX,
                    crop.height * scaleY,
                    0,
                    0,
                    crop.width * scaleX,
                    crop.height * scaleY
                  );

                  return new Promise((resolve, reject) => {
                    let base64Image;

                    try {
                      base64Image = canvas.toDataURL();
                    } catch (e) {
                      reject(e);
                    }

                    if (base64Image) {
                      setCroppedImageUrl(base64Image);
                    }
                  });
                }
              }}
              onImageLoaded={(image) => {
                imageRef.current = image;
              }}
              ruleOfThirds={true}
              src={props.imagePreviewUrl}
            />
          ) : null}
        </div>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            closeDialog();
          }}
        >
          <TranslationWrapper tDefault={"Skip"} tKey={"cropimage.skip"} />
        </Button>
        <Button
          color={"primary"}
          disabled={!(imageRef && crop?.width && crop?.height)}
          onClick={() => {
            closeDialog();
            if (croppedImageUrl) {
              props.setCroppedImage(croppedImageUrl);
            }
          }}
        >
          <TranslationWrapper tDefault={"Crop"} tKey={"cropimage.crop"} />
        </Button>
      </DialogActions>
    </>
  );
}

CropImageDialog.propTypes = {
  setCroppedImage: PropTypes.func.isRequired,
};

export default CropImageDialog;
